/**
 * Context-derived orientation. Orientation is not saved (no angle column), so
 * the things that must face somewhere work it out from where other rooms are.
 */
import { centreOf, directionToward } from './tableGeometry';
import type { Box, Direction, Point } from './tableGeometry';

interface RoomLike {
    type: string;
    position: Point;
    width: number;
    height: number;
}

const STAGE_TYPES = new Set(['stage_tiered', 'stage']);

const boxOf = (r: RoomLike): Box => ({ x: r.position.x, y: r.position.y, width: r.width, height: r.height });

/** Centre of the stage closest to the box, or null when the plan has no stage. */
export function nearestStageCentre(box: Box, rooms: ReadonlyArray<RoomLike>): Point | null {
    const c = centreOf(box);
    let best: Point | null = null;
    let bestD = Infinity;
    for (const r of rooms) {
        if (!STAGE_TYPES.has(r.type)) continue;
        const sc = centreOf(boxOf(r));
        const d = Math.hypot(sc.x - c.x, sc.y - c.y);
        if (d < bestD) { best = sc; bestD = d; }
    }
    return best;
}

/** Centroid of all table centres (where the audience sits), or null with no tables. */
export function audienceCentre(rooms: ReadonlyArray<RoomLike>): Point | null {
    const tables = rooms.filter(r => r.type === 'table');
    if (tables.length === 0) return null;
    const sum = tables.reduce((acc, t) => {
        const c = centreOf(boxOf(t));
        return { x: acc.x + c.x, y: acc.y + c.y };
    }, { x: 0, y: 0 });
    return { x: sum.x / tables.length, y: sum.y / tables.length };
}

/** Which way a crescent's hollow side faces: toward the nearest stage, default up. */
export function crescentFacing(room: RoomLike, rooms: ReadonlyArray<RoomLike>): Direction {
    return directionToward(boxOf(room), nearestStageCentre(boxOf(room), rooms), 'up');
}

/** Where a VIP entrance's route line leads: the audience, else the hall zone, else nowhere. */
export function routeTarget(room: RoomLike, rooms: ReadonlyArray<RoomLike>): Point | null {
    const audience = audienceCentre(rooms);
    if (audience) return audience;
    const hall = rooms.find(r => r !== room && (r.type === 'reception' || r.type === 'dining'));
    return hall ? centreOf(boxOf(hall)) : null;
}

/** First free spot at or after `start`, stepping down and right, so repeated adds do not pile up. */
export function cascadePosition(start: Point, rooms: ReadonlyArray<RoomLike>, step = 20): Point {
    let p = { ...start };
    while (rooms.some(r => r.position.x === p.x && r.position.y === p.y)) {
        p = { x: p.x + step, y: p.y + step };
    }
    return p;
}
